import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { createAsyncThunk } from '@reduxjs/toolkit';
import { alertActions } from '_store';
import { fetchWrapper } from '_helpers';
import { useNavigate } from "react-router-dom";

const submitDronePayload = createAsyncThunk(
  'dronePayload/submit',
  async function (data, { dispatch }) {
    const baseUrl = `${process.env.REACT_APP_API_URL}`;
    try {
      dispatch(alertActions.clear());
      const response = await fetchWrapper.post(`${baseUrl}/dronepayload`, data);
      return response.message;
    } catch (error) {
      if (error.response && error.response.data) {
        throw new Error(error.response.data.message || 'An error occurred');
      }
      throw error;
    }
  }
);

const fetchDronePayloadData = createAsyncThunk(
  'dronePayload/fetchData',
  async function (_, { dispatch }) {
    const baseUrl = `${process.env.REACT_APP_API_URL}`;
    try {
      const response = await fetchWrapper.get(`${baseUrl}/Get-dronepayload-data`);
      return response.data.description || ""; 
    } catch (error) {
      if (error.response && error.response.data) {
        throw new Error(error.response.data.message || 'An error occurred');
      }
      throw error;
    }
  }
);

function DronePayload() {
  const dispatch = useDispatch();
  const auth = useSelector((x) => x.auth.value);
  const [payload, setPayload] = useState("");
  const [wordCount, setWordCount] = useState(0);
  const [responseMessage, setResponseMessage] = useState(""); 
  const [formErrorMessage, setFormErrorMessage] = useState(""); 
  const [isReadOnly, setIsReadOnly] = useState(false); 
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const description = await dispatch(fetchDronePayloadData()).unwrap();
        if (description) {
          setPayload(description);
          setWordCount(description.trim().split(/\s+/).length); 
        }
      } catch (error) {
        if (error.message && error.message !== 'No drone payload data found for the current user') {
          setFormErrorMessage(error.message || 'An error occurred while fetching data');
          setTimeout(() => setFormErrorMessage(""), 5000);
        }
      }
    };
    fetchData();
  }, [dispatch]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setResponseMessage("");
    setFormErrorMessage(""); 

    const data = {
      description: payload,
      email: auth.email,
    };

    try {
      const message = await dispatch(submitDronePayload(data)).unwrap();
      setResponseMessage(message);

      const updatedDescription = await dispatch(fetchDronePayloadData()).unwrap();
      if (updatedDescription) {
        setPayload(updatedDescription);
        setWordCount(updatedDescription.trim().split(/\s+/).length);
      }
      navigate('/list');
    } catch (error) {
      if (error.message) {
        setFormErrorMessage(error.message || 'An unexpected error occurred');
      } else {
        setFormErrorMessage('An unexpected error occurred. Please try again.');
      }
      navigate('/list');
    } finally {
      setTimeout(() => setResponseMessage(""), 5000);
    }
  };

  const handleChange = (e) => {
    const value = e.target.value;
    const maxLength = 5000;
    const wordCount = value.trim().split(/\s+/).length;
    setWordCount(wordCount);
    
    if (value.length <= maxLength) {
      setPayload(value);
      if (value.length === maxLength) {
        setIsReadOnly(true); 
      }
    } else {
      setFormErrorMessage(`Exceeded character limit of ${maxLength}.`);
      setTimeout(() => setFormErrorMessage(""), 5000);
    }
  };


  const handleSkip = async () => {
    setResponseMessage("");
    setFormErrorMessage(""); 

    const data = {
      description: 'None', // Send 'none' when skipping
      email: auth.email,
    };

    try {
      const message = await dispatch(submitDronePayload(data)).unwrap();
      setResponseMessage(message);
      navigate('/list');
    } catch (error) {
      if (error.message) {
        setFormErrorMessage(error.message || 'An unexpected error occurred');
      } else {
        setFormErrorMessage('An unexpected error occurred. Please try again.');
      }
      navigate('/list');
    } finally {
      setTimeout(() => setResponseMessage(""), 5000);
    }
  };

  return (
    <div className="card drone-payload-card">
      <h2 className="card-header drone-payload-heading">Please provide more details about the drones and the payloads you have</h2>
      <div className="card-body">
        <form onSubmit={handleSubmit}>
          <div className="mb-3 ">
            <label>
              <textarea
                name="postContent"
                rows={15}
                cols={140}
                value={payload}
                onChange={handleChange}
                readOnly={isReadOnly}
                className="form-control payload-textarea"
              />
              <div className="word-count">
                {5000 - payload.length} characters remaining
              </div>
            </label>
          </div>
          <div className="text-center d-flex justify-content-center pb-4">
          <button 
            type="submit" 
            className="btn btn-primary submit-btn ms-3"
            disabled={payload.trim() === ""}
          >
            Go to Step 4 <br />
            (Please provide the contact info for your company)
          </button>
          <button
            type="button"
            className="btn btn-primary custom-skip-btn ms-3" 
            onClick={handleSkip}
          >
            Skip the step
          </button>
        </div>
          {formErrorMessage && (
            <div className="alert alert-danger mt-3">
              {formErrorMessage}
            </div>
          )}
        </form>

        {responseMessage && (
          <div className="alert alert-success mt-3">
            {responseMessage}
          </div>
        )}
      </div>
    </div>
  );
}

export { DronePayload };
